/**
 * The dog's shapes, shared by the assembled body and the loose leg on its table: what you
 * pick up is what appears on the dog (design D8 of `robot-dog-assembly`, kept true by
 * design D8 of `robot-dog-follows-player`'s reshape). Every dimension here is a fraction of
 * the caller's own object box — nothing is an absolute magic number, so a profile that
 * changes the box changes the dog with it.
 *
 * Each animated part renders inside its own `<group>` and takes an optional ref
 * (`headRef`, `tailRef`, `hipRef`, `kneeRef`) that a later change's gait
 * (`src/game/dogGait.ts`) writes rotations onto — nothing in this file reads the store, the
 * clock, or animates on its own. A part with no ref supplied renders at an identity
 * rotation: a dog nobody is animating stands still and straight. The static shape that
 * makes the tail read as "carried up" and the neck as "angled" is therefore baked onto the
 * meshes *inside* each ref'd group, never onto the group's own `rotation` prop — that prop
 * is left free for the animator to write to.
 *
 * Every part also takes `castShadow`, which the follow lifecycle turns off while the dog is
 * walking: a moving caster would cost the frozen shadow map its freeze, so a walking dog
 * gets `DogContactShadow` instead (design D7 of `robot-dog-follows-player`).
 */

import type { Ref } from 'react';
import { BackSide } from 'three';
import type { Group } from 'three';
import type { Vec3 } from '../game/profile/types';
import { getGradientMap } from './materials';

// A yellow-orange coat — body, neck, head, muzzle and legs all the one colour — on gunmetal
// joints and sockets. The ears are the face's accent (design D8), and the tail shares their
// orange: warm at both ends of a warm animal, where a slate tail on a yellow-orange dog read as
// a spare part. The ears carry that accent on hue rather than on warmth alone — they are the
// deeper orange, the coat the more yellow one, which is what the ear test pins.
//
// The eyes are black with two white glints apiece, the cartoon shorthand for a wet eye catching
// a light: a big glint high, a small one low, mirrored between the eyes. They used to be the
// portal gate's cyan, repeating that accent — a sound palette argument, and the wrong face. A
// glowing eye is a machine's; a black one with a catchlight is an animal's, and this is an
// animal now. The nose is the same small dark accent it always was.
export const BODY_COLOR = '#e8ab30';
export const JOINT_COLOR = '#333a42';
export const EYE_COLOR = '#0b0d10';
export const GLINT_COLOR = '#ffffff';
export const EAR_COLOR = '#e8913c';
export const TAIL_COLOR = EAR_COLOR;
export const NOSE_COLOR = '#161a1e';

/**
 * The torso capsule's own radius/length/height, so the focus outline drawn alongside
 * `DogTorso` — and anything else that needs to wrap the same shape — uses the exact same
 * numbers rather than a second, driftable copy.
 */
export function torsoCapsuleArgs(size: Vec3): { radius: number; length: number; y: number } {
  const radius = size.x * 0.42;
  // The capsule's straight middle section; clamped so a very short/narrow box never asks
  // for a negative length.
  const length = Math.max(size.z * 0.62 - radius * 2, size.z * 0.15);
  // The capsule's rounded top sits where the old box's top used to.
  const y = size.y / 2 - radius;
  return { radius, length, y };
}

type TorsoProps = {
  size: Vec3;
  isFocused: boolean;
  /** False while the dog is walking: it casts a contact disc instead (design D7 of
   *  `robot-dog-follows-player`), which costs no shadow-map render. */
  castShadow?: boolean;
};

/**
 * A capsule along z in place of a box (design D8): a rounded back and chest rather than a
 * crate, and one primitive rather than several. The focus outline lives here too, sized
 * off the same `torsoCapsuleArgs` the capsule itself uses, so the two never drift apart.
 */
export function DogTorso({ size, isFocused, castShadow = true }: TorsoProps) {
  const { radius, length, y } = torsoCapsuleArgs(size);
  return (
    <>
      <mesh
        name="dog-torso"
        position={[0, y, 0]}
        rotation={[Math.PI / 2, 0, 0]}
        castShadow={castShadow}
        receiveShadow
      >
        <capsuleGeometry args={[radius, length, 4, 8]} />
        <meshToonMaterial
          color={BODY_COLOR}
          gradientMap={getGradientMap()}
          emissive={isFocused ? BODY_COLOR : '#000000'}
          emissiveIntensity={isFocused ? 0.35 : 0}
        />
      </mesh>
      {isFocused && (
        <mesh position={[0, y, 0]} rotation={[Math.PI / 2, 0, 0]} scale={[1.08, 1.08, 1.08]}>
          <capsuleGeometry args={[radius, length, 4, 8]} />
          <meshBasicMaterial color="#ffffff" side={BackSide} />
        </mesh>
      )}
    </>
  );
}

type HeadProps = {
  size: Vec3;
  headRef?: Ref<Group>;
  /** See `DogTorso` — off while the dog walks (design D7). */
  castShadow?: boolean;
};

/**
 * The pivot sits where the head meets the torso (roughly the box's front-top corner); the
 * angled neck, the rounded skull, the tapering muzzle, the nose, the ears and the eyes are
 * all static children positioned inside this group, so the group's own rotation stays free
 * for the gait's head bob/climb pitch (D6). Local -z is toward the front (the front leg
 * sockets and the head both sit at negative z, `DOG_LEG_SOCKETS`), so the muzzle points
 * toward -z and the neck rises toward +y as it moves toward -z.
 */
export function DogHead({ size, headRef, castShadow = true }: HeadProps) {
  const pivotY = size.y * 0.42;
  const pivotZ = -size.z * 0.34;

  const neckLength = size.y * 0.22;
  const neckRadius = size.x * 0.24;
  const neckRise = neckLength * 0.6;
  const neckForward = -neckLength * 0.7;
  const neckAngle = Math.atan2(-neckForward, neckRise);

  const headRadius = size.x * 0.34;
  const headCenter: [number, number, number] = [
    0,
    neckRise + headRadius * 0.5,
    neckForward - headRadius * 0.3,
  ];

  const muzzleLength = size.z * 0.16;
  const muzzleRadius = headRadius * 0.55;
  const muzzleCenter: [number, number, number] = [
    headCenter[0],
    headCenter[1] - headRadius * 0.15,
    headCenter[2] - headRadius * 0.6 - muzzleLength / 2,
  ];

  const noseRadius = muzzleRadius * 0.5;
  const noseCenter: [number, number, number] = [
    muzzleCenter[0],
    muzzleCenter[1],
    muzzleCenter[2] - muzzleLength / 2,
  ];

  // Mickey Mouse ears: two flat discs standing up on top of the skull, each one 0.7 of the
  // head's own radius in radius — so wider across than the skull itself — and a fifth of that
  // thick. They are the largest single feature on the face, which is the point: they are what
  // the eye lands on first (design D8).
  //
  // A disc is round from the one axis it is not flattened along and a sliver from the others,
  // so which way it stands is the whole design. These stand in the plane of the face (a quarter
  // turn about x), which gives a full circle from ahead and a full circle from behind — the two
  // angles a dog walking in front of you is seen from nearly all of the time. They are then
  // splayed outward by 0.3 of a radian (17°) each, which costs the head-on circle 4% of its
  // width (cos 17°, invisible) and buys the profile view an ear with a face to it, not a blade.
  // Mickey's trick of being round from everywhere at once is a 2D trick; this is as close as
  // geometry gets to it without billboarding the ears at the camera.
  //
  // Every offset is measured from the skull's own centre, not from this group's origin: the
  // origin sits down at the neck joint, so an offset taken from there puts the ears at the
  // skull's equator — beside the head, level with the eyes, which reads as earmuffs. From the
  // centre, the discs sit 1.11 radii out along the up-and-outward diagonal, high and splayed
  // like the hat's: the near edge of each is 0.41 radii from the skull's centre, inside it, so
  // the ear meets the head rather than hovering beside it, while the crown stands half a radius
  // clear above the top of the head.
  const earRadius = headRadius * 0.7;
  const earSplay = 0.3;
  const earThickness = earRadius * 0.2;
  const earOffsetX = headRadius * 0.78;
  const earOffsetY = headCenter[1] + headRadius * 0.79;
  const earOffsetZ = headCenter[2] + headRadius * 0.1;

  // Big enough for two glints to read at the distance a dog you are following keeps — the pair
  // is the whole effect, and on the old pinprick eye they would have been one speck.
  const eyeRadius = size.x * 0.075;
  const eyeOffsetX = headRadius * 0.5;
  const eyeCenterZ = headCenter[2] - headRadius * 0.75;

  // Both glints sit inside the eyeball and poke out through its front: a sphere intersecting a
  // sphere needs no decal, no second material pass and no transparency, and from in front it is
  // a round white dot on black. The big one high and outward, the small one low and inward, and
  // mirrored between the eyes — the arrangement every cartoon eye uses, which the eye reads as
  // "wet" long before it reads as "two spheres".
  const bigGlintRadius = eyeRadius * 0.4;
  const smallGlintRadius = eyeRadius * 0.26;

  return (
    <group ref={headRef} position={[0, pivotY, pivotZ]}>
      {/* Neck: a short angled bridge from the torso up to the head */}
      <mesh
        name="dog-neck"
        position={[0, neckRise / 2, neckForward / 2]}
        rotation={[neckAngle, 0, 0]}
        castShadow={castShadow}
        receiveShadow
      >
        <cylinderGeometry args={[neckRadius * 0.85, neckRadius, neckLength, 8]} />
        <meshToonMaterial color={BODY_COLOR} gradientMap={getGradientMap()} />
      </mesh>

      {/* Skull */}
      <mesh name="dog-skull" position={headCenter} castShadow={castShadow} receiveShadow>
        <sphereGeometry args={[headRadius, 8, 6]} />
        <meshToonMaterial color={BODY_COLOR} gradientMap={getGradientMap()} />
      </mesh>

      {/* Muzzle: tapering forward off the skull — the cheapest thing that turns a cube
          into a face — with a dark nose at its tip. */}
      <mesh
        name="dog-muzzle"
        position={muzzleCenter}
        rotation={[Math.PI / 2, 0, 0]}
        castShadow={castShadow}
        receiveShadow
      >
        <cylinderGeometry args={[noseRadius * 1.1, muzzleRadius, muzzleLength, 8]} />
        <meshToonMaterial color={BODY_COLOR} gradientMap={getGradientMap()} />
      </mesh>
      <mesh position={noseCenter} castShadow={false} receiveShadow={false}>
        <sphereGeometry args={[noseRadius, 6, 6]} />
        <meshToonMaterial color={NOSE_COLOR} gradientMap={getGradientMap()} />
      </mesh>

      {/* Ears: two orange discs standing up on top of the skull (design D8). The segment count
          is a little above the rest of the dog's — the circle's edge is the whole silhouette
          here, and a twelve-sided ear reads as a cog. */}
      {[-1, 1].map((side) => (
        <mesh
          key={side}
          name="dog-ear"
          position={[side * earOffsetX, earOffsetY, earOffsetZ]}
          rotation={[Math.PI / 2, 0, -side * earSplay]}
          castShadow={castShadow}
          receiveShadow
        >
          <cylinderGeometry args={[earRadius, earRadius, earThickness, 24]} />
          <meshToonMaterial color={EAR_COLOR} gradientMap={getGradientMap()} />
        </mesh>
      ))}

      {/* Eyes: a black ball with two white glints in it is a face, cheaply (design D8). All
          three are unlit — an eye that falls into shadow with the rest of the head loses the
          face, and the whole point of the glints is that they are the brightest thing on it. */}
      {[-1, 1].map((side) => (
        <group key={side} name="dog-eye" position={[side * eyeOffsetX, headCenter[1], eyeCenterZ]}>
          <mesh name="dog-eye-ball">
            <sphereGeometry args={[eyeRadius, 10, 8]} />
            <meshBasicMaterial color={EYE_COLOR} />
          </mesh>
          <mesh
            name="dog-eye-glint"
            position={[side * eyeRadius * 0.33, eyeRadius * 0.33, -eyeRadius * 0.72]}
          >
            <sphereGeometry args={[bigGlintRadius, 8, 6]} />
            <meshBasicMaterial color={GLINT_COLOR} />
          </mesh>
          <mesh
            name="dog-eye-glint"
            position={[-side * eyeRadius * 0.3, -eyeRadius * 0.32, -eyeRadius * 0.8]}
          >
            <sphereGeometry args={[smallGlintRadius, 8, 6]} />
            <meshBasicMaterial color={GLINT_COLOR} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

type TailProps = {
  size: Vec3;
  tailRef?: Ref<Group>;
  /** See `DogTorso` — off while the dog walks (design D7). */
  castShadow?: boolean;
};

/**
 * The pivot sits at the rump, where the tail leaves the torso; two tapering segments
 * carried up and back are static children, so the group's own rotation stays free for the
 * gait's wag (D6), which rotates the whole assembly as one rigid unit — a wag swings a
 * tail that is already carried up, rather than lifting a hanging one. Local +z is toward
 * the rear (the rear sockets sit at positive z; the head sits at negative z), so both
 * segments rise in +y as they extend toward +z.
 */
export function DogTail({ size, tailRef, castShadow = true }: TailProps) {
  const pivotY = size.y * 0.34;
  const pivotZ = size.z * 0.42;

  const seg1Length = size.z * 0.22;
  const seg1Radius = size.x * 0.12;
  const seg1Rise = 0.6; // radians off the horizontal — carried up, not dragging

  const seg2Length = size.z * 0.18;
  const seg2Radius = seg1Radius * 0.55;
  const seg2Rise = 1.0;

  const seg1End: [number, number, number] = [
    0,
    Math.sin(seg1Rise) * seg1Length,
    Math.cos(seg1Rise) * seg1Length,
  ];

  return (
    <group ref={tailRef} name="dog-tail" position={[0, pivotY, pivotZ]}>
      <mesh
        position={[0, seg1End[1] / 2, seg1End[2] / 2]}
        rotation={[Math.PI / 2 - seg1Rise, 0, 0]}
        castShadow={castShadow}
        receiveShadow
      >
        <cylinderGeometry args={[seg2Radius, seg1Radius, seg1Length, 8]} />
        <meshToonMaterial color={TAIL_COLOR} gradientMap={getGradientMap()} />
      </mesh>
      <group position={seg1End}>
        <mesh
          position={[0, (Math.sin(seg2Rise) * seg2Length) / 2, (Math.cos(seg2Rise) * seg2Length) / 2]}
          rotation={[Math.PI / 2 - seg2Rise, 0, 0]}
          castShadow={castShadow}
          receiveShadow
        >
          <cylinderGeometry args={[seg2Radius * 0.15, seg2Radius, seg2Length, 8]} />
          <meshToonMaterial color={TAIL_COLOR} gradientMap={getGradientMap()} />
        </mesh>
      </group>
    </group>
  );
}

type LegProps = {
  /** The leg object's own box — the same size the loose `RobotDogLeg`'s collider uses, so
   *  an attached leg and the loose one on its table are, deliberately, the exact same
   *  numbers (design D8). */
  size: Vec3;
  /** The hind knee bends opposite to the front (the stifle) — derived by the caller from
   *  the socket id, never guessed here. */
  isHind: boolean;
  hipRef?: Ref<Group>;
  kneeRef?: Ref<Group>;
  /** See `DogTorso` — off while the dog walks (design D7). */
  castShadow?: boolean;
};

/**
 * Two segments and a paw, jointed at the knee. `hipRef`'s group is the pivot the whole leg
 * hangs from: position it at the shoulder/hip (the socket offset already does this for an
 * attached leg) and a rotation about its local x swings the whole leg — positive x turns
 * the foot toward -z, i.e. forward, since the front sockets and the head both sit at
 * negative z (`DOG_LEG_SOCKETS`). `kneeRef`'s group pivots where the thigh ends; a rotation
 * about its local x flexes the shin the same way. The gait (`src/game/dogGait.ts`, not this
 * file) supplies the sign — a front knee's swing-phase flex is positive, a hind knee's is
 * negative — using the `isHind` this component only draws with, never decides.
 *
 * At rest, with no refs written, both groups stay at an identity rotation: a leg drawn
 * straight down is what a dog nobody is animating shows, and what the loose leg on its
 * table shows too (design D8).
 */
export function DogLeg({ size, isHind, hipRef, kneeRef, castShadow = true }: LegProps) {
  const thighLength = size.y * 0.55;
  const shinLength = size.y * 0.45;
  const radius = Math.min(size.x, size.z) * 0.5;
  // Hind legs read heavier at the haunch, tapering to a leaner hock — the one static cue
  // that tells front from back apart without touching the rest pose (design D8).
  const thighTopRadius = radius * (isHind ? 1.2 : 1.0);
  const thighBottomRadius = radius * 0.8;
  const pawRadius = radius * 0.9;

  return (
    <group ref={hipRef}>
      <mesh position={[0, -thighLength / 2, 0]} castShadow={castShadow} receiveShadow>
        <cylinderGeometry args={[thighBottomRadius, thighTopRadius, thighLength, 8]} />
        <meshToonMaterial color={BODY_COLOR} gradientMap={getGradientMap()} />
      </mesh>
      <group ref={kneeRef} position={[0, -thighLength, 0]}>
        <mesh position={[0, -shinLength / 2, 0]} castShadow={castShadow} receiveShadow>
          <cylinderGeometry args={[radius * 0.6, thighBottomRadius, shinLength, 8]} />
          <meshToonMaterial color={BODY_COLOR} gradientMap={getGradientMap()} />
        </mesh>
        <mesh position={[0, -shinLength - pawRadius * 0.4, 0]} castShadow={castShadow} receiveShadow>
          <sphereGeometry args={[pawRadius, 8, 6]} />
          <meshToonMaterial color={BODY_COLOR} gradientMap={getGradientMap()} />
        </mesh>
      </group>
    </group>
  );
}

type ContactShadowProps = {
  /** The host's own box, so the disc is sized and grounded off the same numbers. */
  size: Vec3;
};

/**
 * The shadow a walking dog casts (design D7): one unlit disc on the ground under it, drawn
 * while its meshes are not casting at all. No light, no shadow map, one draw call — which
 * is the entire point, because the sun's map on an outdoor level is rendered four times and
 * then frozen (`FrozenShadows`), and a caster that moves every frame would spend that whole
 * budget permanently on one prop.
 *
 * Rendered as a sibling of the dog's rig rather than inside it, so it stays flat on the
 * ground while the body bobs and rolls above it. `depthWrite` is off so the grass under it
 * still composites, the way a shadow does rather than the way a black plate does.
 */
export function DogContactShadow({ size }: ContactShadowProps) {
  const radius = Math.max(size.x, size.z) * 0.42;
  return (
    <mesh
      name="contact-shadow"
      // A hair above the box's own underside: coplanar with the ground would z-fight it.
      position={[0, -size.y / 2 + 0.01, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      castShadow={false}
      receiveShadow={false}
    >
      <circleGeometry args={[radius, 16]} />
      <meshBasicMaterial color="#000000" transparent opacity={0.34} depthWrite={false} />
    </mesh>
  );
}
